import { type ReactNode } from 'react'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentIllustration,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
} from '@/components/ModalSheet'
import { Button } from '@/components/Button'
import { Form, FormGroup, FormRow } from '@/components/Form'
import { byDesign } from '@/infra/design'
import imgConfirmationUrl from '@/assets/misc/img_confirmation.svg'
import TeamRolesIllustration from '@/components/SpotIllustration/TeamRolesIllustration'

export interface UpdateProgressionStepDialogProps {
  showing: boolean
  showIllustration: boolean
  currentStepTitle: string | undefined
  stepField: ReactNode
  isUpdatePending: boolean
  isOverrideDisabled: boolean
  onClose: () => unknown
  onOverride: () => unknown
}

export function UpdateProgressionStepDialog({
  showing,
  showIllustration,
  currentStepTitle,
  stepField,
  isUpdatePending,
  isOverrideDisabled,
  onClose,
  onOverride,
}: UpdateProgressionStepDialogProps) {
  return (
    <ModalSheet
      showing={showing}
      onRequestClose={isUpdatePending ? undefined : onClose}
    >
      <ModalSheetHeader withCloseButton />

      <ModalSheetContent>
        {showIllustration && (
          <ModalSheetContentIllustration
            src={imgConfirmationUrl}
            illustration={byDesign(undefined, TeamRolesIllustration)}
            alt="Illustration of a finger coloring a checkmark"
          />
        )}

        <ModalSheetContentTitle>
          Update the job's progression
        </ModalSheetContentTitle>

        <ModalSheetContentDescription>
          Select another progression step to override{' '}
          <strong>{currentStepTitle}</strong>
        </ModalSheetContentDescription>

        <ModalSheetContentBlock>
          <Form>
            <FormGroup>
              <FormRow>{stepField}</FormRow>
            </FormGroup>
          </Form>
        </ModalSheetContentBlock>
      </ModalSheetContent>

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Close"
            onPress={onClose}
            isDisabled={isUpdatePending}
          />

          <Button
            label="Override"
            variant="primary"
            onPress={onOverride}
            testId="update-job-progression-dialog-button"
            isPending={isUpdatePending}
            isDisabled={isOverrideDisabled}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}
